import { Loader2 } from "lucide-react";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { useAcknowledgeAlert, useResolveAlert } from "@/hooks/use-alerts";
import { formatDate } from "@/lib/format-date";
import type { DataQualityAlert } from "@/api/types";

const CATEGORY_LABELS: Record<string, string> = {
  overdue_query: "Overdue Query",
  enrollment_behind_target: "Enrollment Behind Target",
  site_inactive: "Site Inactive",
  missing_forms: "Missing Forms",
  visit_window_exceeded: "Visit Window Exceeded",
  screening_failure_spike: "Screening Failure Spike",
};

export function AlertInspector({ data }: { data: unknown }) {
  const alert = data as DataQualityAlert;
  const acknowledge = useAcknowledgeAlert();
  const resolve = useResolveAlert();

  const alertStatus = alert.resolved
    ? "resolved"
    : alert.acknowledged
      ? "acknowledged"
      : "active";

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-foreground">{alert.title}</h3>
        <div className="mt-2 flex items-center gap-2">
          <StatusBadge status={alert.severity} />
          <StatusBadge status={alertStatus} />
        </div>
      </div>

      <p className="text-sm text-muted-foreground">{alert.description}</p>

      <div className="space-y-2">
        <Detail label="Category" value={CATEGORY_LABELS[alert.category] ?? alert.category} />
        <Detail label="Study" value={alert.studyId} />
        {alert.siteId && <Detail label="Site" value={alert.siteId} />}
        <Detail label="Created" value={formatDate(alert.createdAt)} />
        {alert.acknowledgedAt && <Detail label="Acknowledged" value={formatDate(alert.acknowledgedAt)} />}
        {alert.resolvedAt && <Detail label="Resolved" value={formatDate(alert.resolvedAt)} />}
      </div>

      {alert.relatedRecordIds.length > 0 && (
        <div>
          <h4 className="mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Related Records
          </h4>
          <div className="rounded-lg border border-border divide-y divide-border">
            {alert.relatedRecordIds.map((id) => (
              <div key={id} className="px-3 py-2">
                <span className="font-mono text-xs text-foreground">{id}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {!alert.resolved && (
        <div className="flex gap-2 border-t border-border pt-4">
          {!alert.acknowledged && (
            <button
              onClick={() => acknowledge.mutate({ alertId: alert.id })}
              disabled={acknowledge.isPending}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {acknowledge.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Acknowledge
            </button>
          )}
          <button
            onClick={() => resolve.mutate({ alertId: alert.id })}
            disabled={resolve.isPending}
            className="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
          >
            {resolve.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Resolve
          </button>
        </div>
      )}
    </div>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <p className="text-sm text-foreground">{value}</p>
    </div>
  );
}

